import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Chart from 'react-apexcharts';
import HomeLayout from '../../asset/layout/HomeLayout';
import CustomButton from '../../components/mui/CustomButton';
import ResponsiveDatePickers from '../../components/mui/PichDate';
import {
  Box,
  Card,
  CircularProgress,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { getRevenueAnalytics } from '../../actions/analyticsAuth';

const PERIODS = [
  { value: 'TODAY', label: 'Today' },
  { value: 'YESTERDAY', label: 'Yesterday' },
  { value: 'THIS_WEEK', label: 'This Week' },
  { value: 'THIS_MONTH', label: 'This Month' },
  { value: 'CUSTOM', label: 'Custom' },
];

const SECTION_META = {
  Revenue: { accent: '#11409B', gradient: 'linear-gradient(135deg, #3b82f6 0%, #11409B 100%)' },
  Vendor: { accent: '#0f766e', gradient: 'linear-gradient(135deg, #2dd4bf 0%, #0f766e 100%)' },
  'Payment Gateway': { accent: '#2563eb', gradient: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)' },
  Refunds: { accent: '#dc2626', gradient: 'linear-gradient(135deg, #f87171 0%, #dc2626 100%)' },
  Rewards: { accent: '#d97706', gradient: 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)' },
  Profit: { accent: '#16a34a', gradient: 'linear-gradient(135deg, #4ade80 0%, #16a34a 100%)' },
  Reconciliation: { accent: '#64748b', gradient: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)' },
};

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.03, duration: 0.4, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: '24px',
      border: '1px solid',
      borderColor: alpha('#11409B', 0.08),
      bgcolor: '#fff',
      boxShadow: '0 4px 32px rgba(15, 23, 42, 0.06)',
      overflow: 'hidden',
      ...sx,
    }}
  >
    {children}
  </Card>
);

const n = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatValue = (value, format) => {
  if (value == null || value === '') return '—';
  if (format === 'count') {
    const count = Number(value);
    return Number.isFinite(count) ? count.toLocaleString('en-IN') : String(value);
  }
  if (format === 'percent') {
    const pct = Number(value);
    return Number.isFinite(pct) ? `${pct.toFixed(2)}%` : '—';
  }
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return `₹${amount.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const formatRupeeAxis = (value) => {
  const amount = Number(value) || 0;
  if (Math.abs(amount) >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (Math.abs(amount) >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (Math.abs(amount) >= 1000) return `₹${(amount / 1000).toFixed(1)}k`;
  return `₹${amount.toLocaleString('en-IN')}`;
};

const MetricCard = ({ label, value, format, accent, gradient, index }) => (
  <motion.div custom={index} initial="hidden" animate="visible" variants={fadeUp}>
    <PanelCard sx={{ height: '100%' }}>
      <Box sx={{ p: 2.25 }}>
        <Stack direction="row" spacing={1.75} alignItems="flex-start">
          <Box
            sx={{
              width: 10,
              alignSelf: 'stretch',
              borderRadius: '999px',
              background: gradient,
              minHeight: 48,
              flexShrink: 0,
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                color: '#64748b',
                fontWeight: 700,
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                lineHeight: 1.3,
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                mt: 0.75,
                fontWeight: 800,
                fontSize: { xs: '1.35rem', md: '1.55rem' },
                color: accent,
                letterSpacing: '-0.03em',
                lineHeight: 1.15,
                wordBreak: 'break-word',
              }}
            >
              {formatValue(value, format)}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </PanelCard>
  </motion.div>
);

const ChartCard = ({ title, subtitle, children, index = 0 }) => (
  <motion.div custom={index} initial="hidden" animate="visible" variants={fadeUp}>
    <PanelCard sx={{ height: '100%' }}>
      <Box sx={{ p: { xs: 2, md: 2.5 } }}>
        <Typography sx={{ fontWeight: 800, color: '#0f172a', mb: 0.35 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', mb: 1.5 }}>
            {subtitle}
          </Typography>
        ) : (
          <Box sx={{ mb: 1.5 }} />
        )}
        {children}
      </Box>
    </PanelCard>
  </motion.div>
);

const baseChartOptions = {
  chart: {
    toolbar: { show: false },
    fontFamily: 'inherit',
    animations: { enabled: true, speed: 650 },
  },
  dataLabels: { enabled: false },
  grid: {
    borderColor: alpha('#11409B', 0.08),
    strokeDashArray: 4,
  },
  legend: {
    fontWeight: 600,
    labels: { colors: '#64748b' },
  },
  tooltip: {
    theme: 'light',
  },
};

const RevenueAnalyticsPanel = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [period, setPeriod] = useState('THIS_MONTH');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!embedded && !localStorage.getItem('token')) {
      navigate('/');
    }
  }, [navigate, embedded]);

  const fetchAnalytics = useCallback(() => {
    if (period === 'CUSTOM' && !dateFrom && !dateTo) {
      enqueueSnackbar('Select a custom date range', { variant: 'warning' });
      return;
    }

    setLoading(true);
    const query = { period };
    if (period === 'CUSTOM') {
      if (dateFrom) query.createdFrom = dayjs(dateFrom).startOf('day').toISOString();
      if (dateTo) query.createdTo = dayjs(dateTo).endOf('day').toISOString();
    }

    dispatch(
      getRevenueAnalytics(
        query,
        (response) => {
          const payload = response?.data?.data ?? response?.data ?? response ?? null;
          setData(payload);
          setLoading(false);
        },
        (err) => {
          console.log('Error fetching revenue analytics:', err);
          enqueueSnackbar(
            err?.response?.data?.message || 'Failed to load revenue analytics',
            { variant: 'error' }
          );
          setData(null);
          setLoading(false);
        }
      )
    );
  }, [dispatch, period, dateFrom, dateTo]);

  useEffect(() => {
    if (period !== 'CUSTOM') {
      fetchAnalytics();
    }
  }, [period, fetchAnalytics]);

  const cardsBySection = useMemo(() => {
    const cards = Array.isArray(data?.cards) ? data.cards : [];
    const grouped = {};
    cards.forEach((card) => {
      const section = card.section || 'Other';
      if (!grouped[section]) grouped[section] = [];
      grouped[section].push(card);
    });
    return grouped;
  }, [data]);

  const sectionOrder = [
    'Revenue',
    'Vendor',
    'Payment Gateway',
    'Refunds',
    'Rewards',
    'Profit',
    'Reconciliation',
  ];

  const charts = useMemo(() => {
    if (!data) return null;

    const revenue = data.revenue || {};
    const vendor = data.vendor || {};
    const gateway = data.paymentGateway || {};
    const refunds = data.refunds || {};
    const rewards = data.rewards || {};
    const profit = data.profit || {};
    const reconciliation = data.reconciliation || {};

    const ordersDonut = {
      series: [n(revenue.successfulOrders), n(revenue.failedOrders)],
      options: {
        ...baseChartOptions,
        labels: ['Successful', 'Failed'],
        colors: ['#16a34a', '#dc2626'],
        chart: { ...baseChartOptions.chart, type: 'donut' },
        plotOptions: {
          pie: {
            donut: {
              size: '68%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Orders',
                  fontWeight: 700,
                  color: '#0f172a',
                  formatter: () =>
                    (n(revenue.successfulOrders) + n(revenue.failedOrders)).toLocaleString(
                      'en-IN'
                    ),
                },
              },
            },
          },
        },
        legend: { ...baseChartOptions.legend, position: 'bottom' },
      },
    };

    const moneyOverview = {
      series: [
        {
          name: 'Amount',
          data: [
            n(revenue.totalSales),
            n(revenue.totalRevenue),
            n(vendor.vendorCost),
            n(gateway.netSettlementReceived),
            n(profit.netProfit),
          ],
        },
      ],
      options: {
        ...baseChartOptions,
        chart: { ...baseChartOptions.chart, type: 'bar' },
        colors: ['#11409B', '#2563eb', '#0f766e', '#4f46e5', '#16a34a'],
        plotOptions: {
          bar: {
            borderRadius: 8,
            columnWidth: '48%',
            distributed: true,
          },
        },
        xaxis: {
          categories: ['Total Sales', 'Revenue', 'Vendor Cost', 'Net Settlement', 'Net Profit'],
          labels: { style: { colors: '#64748b', fontWeight: 600, fontSize: '11px' } },
        },
        yaxis: {
          labels: {
            formatter: formatRupeeAxis,
            style: { colors: '#94a3b8' },
          },
        },
        legend: { show: false },
        tooltip: {
          y: { formatter: (val) => formatValue(val, 'currency') },
        },
      },
    };

    const gatewayBar = {
      series: [
        {
          name: 'Gateway',
          data: [
            n(gateway.razorpayCollection),
            n(gateway.razorpayGatewayCharges),
            n(gateway.gstOnGatewayCharges),
            n(gateway.netSettlementReceived),
          ],
        },
      ],
      options: {
        ...baseChartOptions,
        chart: { ...baseChartOptions.chart, type: 'bar' },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 8,
            barHeight: '55%',
            distributed: true,
          },
        },
        colors: ['#2563eb', '#f59e0b', '#ea580c', '#16a34a'],
        xaxis: {
          categories: ['Collection', 'Charges', 'GST', 'Net Settlement'],
          labels: {
            formatter: formatRupeeAxis,
            style: { colors: '#94a3b8' },
          },
        },
        yaxis: {
          labels: { style: { colors: '#64748b', fontWeight: 600 } },
        },
        legend: { show: false },
        tooltip: {
          y: { formatter: (val) => formatValue(val, 'currency') },
        },
      },
    };

    const refundsRewards = {
      series: [
        {
          name: 'Refunds',
          data: [n(refunds.razorpayRefundAmount), n(refunds.rewardPointRefundAmount)],
        },
        {
          name: 'Rewards',
          data: [n(rewards.rewardPointsIssued), n(rewards.rewardPointsRedeemed)],
        },
      ],
      options: {
        ...baseChartOptions,
        chart: { ...baseChartOptions.chart, type: 'bar' },
        colors: ['#dc2626', '#d97706'],
        plotOptions: {
          bar: {
            borderRadius: 8,
            columnWidth: '55%',
          },
        },
        xaxis: {
          categories: ['Razorpay / Issued', 'Reward pts / Redeemed'],
          labels: { style: { colors: '#64748b', fontWeight: 600, fontSize: '11px' } },
        },
        yaxis: {
          labels: {
            formatter: formatRupeeAxis,
            style: { colors: '#94a3b8' },
          },
        },
        tooltip: {
          y: { formatter: (val) => formatValue(val, 'currency') },
        },
      },
    };

    const profitRadial = {
      series: [Math.max(0, Math.min(100, n(profit.profitMarginPercent)))],
      options: {
        ...baseChartOptions,
        chart: { ...baseChartOptions.chart, type: 'radialBar' },
        colors: ['#16a34a'],
        plotOptions: {
          radialBar: {
            hollow: { size: '62%' },
            track: { background: alpha('#16a34a', 0.12) },
            dataLabels: {
              name: {
                show: true,
                color: '#64748b',
                fontWeight: 700,
                offsetY: -8,
              },
              value: {
                show: true,
                fontSize: '22px',
                fontWeight: 800,
                color: '#0f172a',
                formatter: (val) => `${Number(val).toFixed(1)}%`,
              },
            },
          },
        },
        labels: ['Margin'],
      },
    };

    const reconciliationBar = {
      series: [
        {
          name: 'Count',
          data: [
            n(reconciliation.settlementPending),
            n(reconciliation.settlementMismatch),
            n(reconciliation.failedOrdersPending),
            n(reconciliation.apiFailedOrders),
          ],
        },
      ],
      options: {
        ...baseChartOptions,
        chart: { ...baseChartOptions.chart, type: 'bar' },
        colors: ['#f59e0b', '#dc2626', '#2563eb', '#64748b'],
        plotOptions: {
          bar: {
            borderRadius: 8,
            columnWidth: '48%',
            distributed: true,
          },
        },
        xaxis: {
          categories: ['Pending', 'Mismatch', 'Failed pending', 'API failed'],
          labels: { style: { colors: '#64748b', fontWeight: 600, fontSize: '11px' } },
        },
        yaxis: {
          labels: {
            formatter: (val) => Number(val).toLocaleString('en-IN'),
            style: { colors: '#94a3b8' },
          },
        },
        legend: { show: false },
        tooltip: {
          y: { formatter: (val) => Number(val).toLocaleString('en-IN') },
        },
      },
    };

    return {
      ordersDonut,
      moneyOverview,
      gatewayBar,
      refundsRewards,
      profitRadial,
      reconciliationBar,
      profitCompare: {
        series: [
          {
            name: 'Profit',
            data: [n(profit.grossProfit), n(profit.netProfit)],
          },
        ],
        options: {
          ...baseChartOptions,
          chart: { ...baseChartOptions.chart, type: 'bar' },
          colors: ['#22c55e', '#15803d'],
          plotOptions: {
            bar: {
              borderRadius: 10,
              columnWidth: '42%',
              distributed: true,
            },
          },
          xaxis: {
            categories: ['Gross Profit', 'Net Profit'],
            labels: { style: { colors: '#64748b', fontWeight: 600 } },
          },
          yaxis: {
            labels: {
              formatter: formatRupeeAxis,
              style: { colors: '#94a3b8' },
            },
          },
          legend: { show: false },
          tooltip: {
            y: { formatter: (val) => formatValue(val, 'currency') },
          },
        },
      },
    };
  }, [data]);

  return (
      <Box
        sx={
          embedded
            ? { width: '100%' }
            : {
                minHeight: '100%',
                background:
                  'linear-gradient(180deg, #f0f4fa 0%, #f5f7fa 35%, #eef2f7 100%)',
                mx: { xs: -2, md: -3 },
                mt: { xs: -2, md: -3 },
                px: { xs: 2, md: 3 },
                pt: { xs: 2, md: 3 },
                pb: 4,
              }
        }
      >
        {!embedded && (
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <Box
            sx={{
              p: { xs: 2.5, md: 3.5 },
              mb: 3,
              borderRadius: '24px',
              background:
                'linear-gradient(135deg, #0c3483 0%, #11409B 42%, #1e40af 72%, #2563eb 100%)',
              color: '#fff',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(17, 64, 155, 0.32)',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -40,
                right: -20,
                width: 180,
                height: 180,
                borderRadius: '50%',
                bgcolor: alpha('#fff', 0.06),
              }}
            />
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
              sx={{ position: 'relative', zIndex: 1 }}
            >
              <Stack spacing={1} sx={{ maxWidth: 640 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <InsightsOutlinedIcon />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, letterSpacing: '0.12em', opacity: 0.88 }}
                  >
                    ANALYTICS
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '-0.03em',
                    fontSize: { xs: '1.6rem', md: '2rem' },
                  }}
                >
                  Revenue Analytics
                </Typography>
                <Typography sx={{ opacity: 0.84, lineHeight: 1.55 }}>
                  Sales, vendor cost, Razorpay settlement, refunds, rewards, profit and
                  reconciliation — with cards and charts.
                </Typography>
              </Stack>

              {data && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '16px',
                    bgcolor: alpha('#fff', 0.12),
                    border: `1px solid ${alpha('#fff', 0.18)}`,
                    minWidth: { xs: '100%', md: 220 },
                  }}
                >
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, opacity: 0.8 }}>
                    NET PROFIT
                  </Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', mt: 0.5 }}>
                    {formatValue(data?.profit?.netProfit, 'currency')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', opacity: 0.8, mt: 0.5 }}>
                    Margin {formatValue(data?.profit?.profitMarginPercent, 'percent')}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        </motion.div>
        )}

        <PanelCard sx={{ mb: 3, p: { xs: 2, md: 2.5 } }}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', lg: 'center' }}
            justifyContent="space-between"
          >
            <ToggleButtonGroup
              exclusive
              value={period}
              onChange={(_, value) => {
                if (value) setPeriod(value);
              }}
              sx={{
                flexWrap: 'wrap',
                gap: 1,
                '& .MuiToggleButton-root': {
                  border: `1px solid ${alpha('#11409B', 0.18)} !important`,
                  borderRadius: '12px !important',
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 1.75,
                  py: 0.75,
                  color: '#475569',
                  '&.Mui-selected': {
                    bgcolor: '#11409B',
                    color: '#fff',
                    '&:hover': { bgcolor: '#0c3483' },
                  },
                },
              }}
            >
              {PERIODS.map((item) => (
                <ToggleButton key={item.value} value={item.value}>
                  {item.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
              {period === 'CUSTOM' && (
                <>
                  <ResponsiveDatePickers
                    selectedDate={dateFrom}
                    setSelectedDate={setDateFrom}
                  />
                  <ResponsiveDatePickers
                    selectedDate={dateTo}
                    setSelectedDate={setDateTo}
                  />
                  <CustomButton
                    variant="contained"
                    onClick={fetchAnalytics}
                    sx={{
                      height: '2.75rem',
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 700,
                      bgcolor: '#11409B',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#0c3483' },
                    }}
                  >
                    Apply
                  </CustomButton>
                </>
              )}
              <CustomButton
                variant="outlined"
                startIcon={<RefreshOutlinedIcon />}
                onClick={fetchAnalytics}
                sx={{
                  height: '2.75rem',
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 700,
                  borderColor: alpha('#11409B', 0.25),
                  color: '#11409B',
                }}
              >
                Refresh
              </CustomButton>
            </Stack>
          </Stack>

          {data?.from && (
            <Typography sx={{ mt: 1.5, color: '#94a3b8', fontSize: '0.8rem' }}>
              Showing {dayjs(data.from).format('DD MMM YYYY, hh:mm A')}
              {data.to ? ` → ${dayjs(data.to).format('DD MMM YYYY, hh:mm A')}` : ''}
              {data.timezone ? ` (${data.timezone})` : ''}
              {data.period ? ` · ${data.period.replace('_', ' ')}` : ''}
            </Typography>
          )}
        </PanelCard>

        {loading ? (
          <PanelCard>
            <Stack alignItems="center" sx={{ py: 10 }}>
              <CircularProgress sx={{ color: '#11409B' }} />
              <Typography sx={{ mt: 2, color: '#64748b' }}>Loading analytics…</Typography>
            </Stack>
          </PanelCard>
        ) : !data ? (
          <PanelCard>
            <Stack alignItems="center" sx={{ py: 8 }}>
              <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                No analytics data
              </Typography>
              <Typography sx={{ color: '#64748b', mt: 1 }}>
                Try another period or refresh.
              </Typography>
            </Stack>
          </PanelCard>
        ) : (
          <Stack spacing={3}>
            {charts && (
              <Box>
                <Typography sx={{ fontWeight: 800, color: '#0f172a', mb: 1.5, fontSize: '1.05rem' }}>
                  Charts
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <ChartCard title="Orders mix" subtitle="Successful vs failed" index={0}>
                      <Chart
                        type="donut"
                        height={280}
                        series={charts.ordersDonut.series}
                        options={charts.ordersDonut.options}
                      />
                    </ChartCard>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <ChartCard
                      title="Money overview"
                      subtitle="Sales, revenue, cost, settlement & profit"
                      index={1}
                    >
                      <Chart
                        type="bar"
                        height={280}
                        series={charts.moneyOverview.series}
                        options={charts.moneyOverview.options}
                      />
                    </ChartCard>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ChartCard
                      title="Payment gateway"
                      subtitle="Razorpay collection to net settlement"
                      index={2}
                    >
                      <Chart
                        type="bar"
                        height={280}
                        series={charts.gatewayBar.series}
                        options={charts.gatewayBar.options}
                      />
                    </ChartCard>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ChartCard
                      title="Refunds & rewards"
                      subtitle="Refund amounts vs reward activity"
                      index={3}
                    >
                      <Chart
                        type="bar"
                        height={280}
                        series={charts.refundsRewards.series}
                        options={charts.refundsRewards.options}
                      />
                    </ChartCard>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <ChartCard title="Profit margin" subtitle="Net margin %" index={4}>
                      <Chart
                        type="radialBar"
                        height={280}
                        series={charts.profitRadial.series}
                        options={charts.profitRadial.options}
                      />
                    </ChartCard>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <ChartCard title="Gross vs net" subtitle="Profit comparison" index={5}>
                      <Chart
                        type="bar"
                        height={280}
                        series={charts.profitCompare.series}
                        options={charts.profitCompare.options}
                      />
                    </ChartCard>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <ChartCard
                      title="Reconciliation"
                      subtitle="Ops counts needing attention"
                      index={6}
                    >
                      <Chart
                        type="bar"
                        height={280}
                        series={charts.reconciliationBar.series}
                        options={charts.reconciliationBar.options}
                      />
                    </ChartCard>
                  </Grid>
                </Grid>
              </Box>
            )}

            {sectionOrder.map((section) => {
              const cards = cardsBySection[section] || [];
              if (cards.length === 0) return null;
              const meta = SECTION_META[section] || SECTION_META.Revenue;
              return (
                <Box key={section}>
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        background: meta.gradient,
                      }}
                    />
                    <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: '1.05rem' }}>
                      {section}
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    {cards.map((card, index) => (
                      <Grid item key={card.key || card.label} xs={12} sm={6} md={4} lg={3}>
                        <MetricCard
                          label={card.label}
                          value={card.value}
                          format={card.format}
                          accent={meta.accent}
                          gradient={meta.gradient}
                          index={index}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
  );
};

const RevenueAnalytics = () => (
  <HomeLayout>
    <RevenueAnalyticsPanel />
  </HomeLayout>
);

export { RevenueAnalyticsPanel };
export default RevenueAnalytics;
